import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';

type InvoiceContextType = {
  isOpen: boolean;
  openInvoiceDetailDrawer: (
    invoiceId: string,
    options?: { writeOffId?: string; writeOffStatus?: any; tabType?: any; actions?: any }
  ) => void;
  closeInvoiceDetailDrawer: () => void;
  invoiceId: string | null;
  writeOffId?: string | null;
  writeOffStatus?: any;
  tabType?: any;
  actions?: any;
};

const InvoiceContext = createContext<InvoiceContextType | undefined>(undefined);

export const InvoiceDetailProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [writeOffId, setWriteOffId] = useState<string | null>(null);
  const [writeOffStatus, setWriteOffStatus] = useState<any>(undefined);
  const [tabType, setTabType] = useState<any>(undefined);
  const [actions, setActions] = useState<any>(undefined);

  const openInvoiceDetailDrawer = useCallback(
    (invoiceId: string, options?: { writeOffId?: string; writeOffStatus?: any; tabType?: any; actions?: any }) => {
      setInvoiceId(invoiceId);
      setWriteOffId(options?.writeOffId || null);
      setWriteOffStatus(options?.writeOffStatus);
      setTabType(options?.tabType);
      setActions(options?.actions);
    },
    []
  );

  const closeInvoiceDetailDrawer = useCallback(() => {
    setInvoiceId(null);
    setWriteOffId(null);
    setWriteOffStatus(undefined);
    setTabType(undefined);
    setActions(undefined);
  }, []);

  const value = useMemo(
    () => ({
      isOpen: !!invoiceId,
      invoiceId,
      writeOffId,
      writeOffStatus,
      tabType,
      actions,
      openInvoiceDetailDrawer,
      closeInvoiceDetailDrawer,
    }),
    [invoiceId, writeOffId, writeOffStatus, tabType, actions, closeInvoiceDetailDrawer, openInvoiceDetailDrawer]
  );

  return <InvoiceContext.Provider value={value}>{children}</InvoiceContext.Provider>;
};

export const useInvoiceContext = (): InvoiceContextType => {
  const context = useContext(InvoiceContext);
  if (!context) {
    throw new Error('useInvoiceContext must be used within a InvoiceDetailProvider');
  }
  return context;
};
